/**
 * ShareCopyLinkButton Component
 *
 * Simple copy-link button for shared link pages (/s/{shortId}).
 * Copies the existing share URL to clipboard instead of generating a new one.
 */

import React, { useState, useCallback } from 'react';
import {
  Pressable,
  Text,
  StyleSheet,
  Platform,
  View,
  type PressableStateCallbackType,
} from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { LinearGradient } from 'expo-linear-gradient';
import { useTranslation } from '@dramadetector/shared';
import { getAppUrl } from '../../lib/url';
import { captureEvent } from '../../lib/analytics';

interface ShareCopyLinkButtonProps {
  shortId: string;
  testID?: string;
}

export function ShareCopyLinkButton({
  shortId,
  testID = 'share-copy-link-button',
}: ShareCopyLinkButtonProps) {
  const { t } = useTranslation('share');
  const [copied, setCopied] = useState(false);

  const shortUrl = `${getAppUrl()}/s/${shortId}`;

  const handleCopyLink = useCallback(async () => {
    try {
      if (Platform.OS === 'web') {
        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(shortUrl);
        } else {
          await Clipboard.setStringAsync(shortUrl);
        }
      } else {
        await Clipboard.setStringAsync(shortUrl);
      }

      setCopied(true);
      captureEvent('sharing:public_link_copied', {});

      setTimeout(() => setCopied(false), 2000);
    } catch {
      captureEvent('sharing:public_link_copy_failed', {});
    }
  }, [shortId, shortUrl]);

  return (
    <Pressable
      onPress={handleCopyLink}
      disabled={copied}
      testID={testID}
      style={({ pressed }: PressableStateCallbackType) => [
        styles.container,
        pressed && styles.pressed,
        copied && styles.copied,
      ]}
    >
      <LinearGradient
        colors={copied ? ['#10B981', '#059669'] : ['#a855f7', '#ec4899']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.gradient}
      >
        <View style={styles.content}>
          <Text style={styles.icon}>{copied ? '✓' : '🔗'}</Text>
          <Text style={styles.text}>
            {copied
              ? t('share.link_option.copied', { defaultValue: 'Copied!' })
              : t('share.link_option.label', {
                  defaultValue: 'Copy public link',
                })}
          </Text>
        </View>
      </LinearGradient>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 9999,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
  },
  pressed: {
    transform: [{ scale: 0.98 }],
    opacity: 0.9,
  },
  copied: {
    shadowOpacity: 0.2,
  },
  gradient: {
    paddingVertical: 16,
    paddingHorizontal: 32,
    minHeight: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  icon: {
    fontSize: 18,
  },
  text: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '700',
    textAlign: 'center',
  },
});
